/**
 * KPI Widget Component
 * Displays a single aggregated value with optional trend indicator
 */

import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import clsx from 'clsx';
import type { Threshold } from '../../types';

export interface KPIWidgetProps {
  value?: number | string;
  label?: string;
  format?: string;
  prefix?: string;
  suffix?: string;
  trend?: number;
  trendLabel?: string;
  thresholds?: Threshold[];
  loading?: boolean;
}

function formatValue(value: number | string | undefined, format?: string, prefix?: string, suffix?: string): string {
  if (value === undefined || value === null) return '—';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) return String(value);

  let formatted: string;

  if (format === '#,##0' || format === '#,##0.00') {
    formatted = numValue.toLocaleString('en-GB', {
      minimumFractionDigits: format.includes('.') ? 2 : 0,
      maximumFractionDigits: format.includes('.') ? 2 : 0,
    });
  } else if (format === '0%') {
    formatted = `${(numValue * 100).toFixed(0)}%`;
  } else if (format === '0.0%') {
    formatted = `${(numValue * 100).toFixed(1)}%`;
  } else {
    formatted = String(numValue);
  }

  return `${prefix || ''}${formatted}${suffix || ''}`;
}

function getThresholdColor(value: number | string | undefined, thresholds?: Threshold[]): string | undefined {
  if (!thresholds || thresholds.length === 0 || value === undefined) return undefined;

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return undefined;

  // Sort thresholds by value ascending
  const sorted = [...thresholds].sort((a, b) => a.value - b.value);

  let color: string | undefined;
  for (const threshold of sorted) {
    if (numValue >= threshold.value) {
      color = threshold.color;
    }
  }

  return color;
}

export function KPIWidget({
  value,
  label,
  format,
  prefix,
  suffix,
  trend,
  trendLabel,
  thresholds,
  loading,
}: KPIWidgetProps) {
  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-4 animate-pulse">
        <div className="h-10 w-24 bg-gray-200 rounded mb-2" />
        <div className="h-4 w-16 bg-gray-200 rounded" />
      </div>
    );
  }

  const formattedValue = formatValue(value, format, prefix, suffix);
  const color = getThresholdColor(value, thresholds);

  const TrendIcon = trend !== undefined
    ? trend > 0
      ? TrendingUp
      : trend < 0
        ? TrendingDown
        : Minus
    : null;

  return (
    <div className="h-full flex flex-col items-center justify-center p-4">
      {/* Value */}
      <div
        className={clsx('text-4xl font-bold', {
          'text-gray-900': !color,
        })}
        style={color ? { color } : undefined}
      >
        {formattedValue}
      </div>

      {/* Label */}
      {label && <div className="text-sm text-gray-500 mt-1">{label}</div>}

      {/* Trend */}
      {trend !== undefined && (
        <div className="mt-2 flex items-center gap-1">
          {TrendIcon && (
            <TrendIcon
              className={clsx('w-4 h-4', {
                'text-green-500': trend > 0,
                'text-red-500': trend < 0,
                'text-gray-400': trend === 0,
              })}
            />
          )}
          <span
            className={clsx('text-sm font-medium', {
              'text-green-500': trend > 0,
              'text-red-500': trend < 0,
              'text-gray-400': trend === 0,
            })}
          >
            {Math.abs(trend)}%
          </span>
          {trendLabel && <span className="text-sm text-gray-500 ml-1">{trendLabel}</span>}
        </div>
      )}
    </div>
  );
}
